// Deterministische Konsistenz-Hinweise („Dialogisches Alignment") — rein
// client-seitig, ohne LLM und ohne Backend-Roundtrip. Die Regeln spiegeln die
// Audit-Heuristiken des Backends (app/audit/matrix.py, classification.py,
// pipeline.py), damit der User schon beim Konstruieren sieht, was der Audit
// später bemängeln würde. Funktioniert generisch über einem Key-Typ K:
// Wizard = Array-Index (number), Editor = UUID (string).

export type HintSeverity = "ok" | "tension" | "misalignment";

export type HintCode =
  | "ilo_without_tla"
  | "ilo_unassessed"
  | "tla_without_ilo"
  | "at_without_ilo"
  | "bloom_distance_ilo_tla"
  | "bloom_distance_ilo_at";

export interface Hint<K = string> {
  code: HintCode;
  severity: Exclude<HintSeverity, "ok">;
  iloKey?: K;
  tlaKey?: K;
  atKey?: K;
  // Bloom-Distanz des Paars (nur bei bloom_distance_* gesetzt)
  distance?: number;
  // Wizard-/Editor-Step, in dem sich der Hinweis beheben lässt (Deeplink)
  step: number;
}

export interface IloInput {
  bloom_level?: number;
  bender_depth?: string;
}

export interface TlaInput<K> {
  type?: string;
  bloom_levels?: number[];
  linked_ilo_keys: K[];
}

export interface AtInput<K> {
  type?: string;
  bloom_levels?: number[];
  linked_ilo_keys: K[];
}

// Spiegelt severity_from_distance in app/audit/matrix.py:34.
// distance null = Bloom unbekannt → nicht bewertbar (kein Live-Hinweis; das
// Backend wertet das im Audit als misalignment, hier wäre das Rauschen).
export function severityFromDistance(distance: number | null): HintSeverity | null {
  if (distance === null) return null;
  if (distance <= 1) return "ok";
  if (distance === 2) return "tension";
  return "misalignment";
}

// Bender→Bloom-Näherung: Mittelpunkt der BLOOM_TO_BENDER-Spanne
// (invers zu app/audit/classification.py:12). Wissen→1, Verstehen→2,
// Können→3 (Mitte von 3-4), Performanz→5 (Mitte von 5-6).
const BENDER_TO_BLOOM_MIDPOINT: Record<string, number> = {
  Wissen: 1,
  Verstehen: 2,
  Können: 3,
  Performanz: 5,
};

// Typ→Bloom-Fallbacks, gespiegelt aus den Backend-Klassifikationen
// (Quelle: app/audit/pipeline.py — _classify_tlas :72-104, _classify_ats :50-63).
// Bei Änderungen dort hier nachziehen.
const TLA_TYPE_TO_BLOOM: Record<string, number[]> = {
  lecture_classic: [1, 2],
  discussion_plenum: [2, 3, 4, 5],
  case_study: [3, 4, 5],
  role_play: [3, 4, 5, 6],
  project_work: [4, 5, 6],
  flipped_video: [1, 2],
  quiz_formative: [1, 2, 3],
  reading_text: [1, 2],
  breakout_groups: [2, 3, 4],
  simulation: [3, 4, 5],
  peer_review: [4, 5],
  concept_mapping: [2, 3, 4],
  think_pair_share: [2, 3],
  reflection_writing: [4, 5],
  brainstorming: [2, 3, 6],
  transfer_task: [3, 4, 5, 6],
  supervised_practice: [3, 4, 5],
  world_cafe: [3, 4, 5],
  fishbowl: [4, 5],
  pyramid_discussion: [3, 4, 5],
  jigsaw: [2, 3, 4],
  presentation_student: [3, 4, 5],
  design_sprint: [5, 6],
  demonstration: [2, 3],
  expert_interview: [3, 4, 5],
  portfolio: [4, 5, 6],
  lab_experiment: [3, 4],
  live_qa: [1, 2],
};

const AT_TYPE_TO_BLOOM: Record<string, number[]> = {
  mc_test: [1, 2],
  short_answer: [1, 2],
  essay: [3, 4, 5],
  case_analysis: [4, 5],
  project: [4, 5, 6],
  portfolio: [4, 5, 6],
  oral_exam: [2, 3, 4],
  peer_review: [4, 5],
  presentation: [3, 4],
};

function iloBloom(ilo: IloInput): number | null {
  if (typeof ilo.bloom_level === "number") return ilo.bloom_level;
  if (ilo.bender_depth && ilo.bender_depth in BENDER_TO_BLOOM_MIDPOINT) {
    return BENDER_TO_BLOOM_MIDPOINT[ilo.bender_depth];
  }
  return null;
}

// Wie das Backend (matrix.py:60): für die Distanz zählt das Maximum der
// Bloom-Levels; ohne explizite Levels der Typ-Fallback aus pipeline.py.
function maxBloom(
  levels: number[] | undefined,
  type: string | undefined,
  typeMap: Record<string, number[]>,
): number | null {
  if (levels && levels.length > 0) return Math.max(...levels);
  if (type && typeMap[type]) return Math.max(...typeMap[type]);
  return null;
}

export interface PairInfo<K> {
  key: K;
  distance: number | null; // null = Bloom unbekannt, nicht bewertbar
  severity: HintSeverity | null;
}

export interface IloAlignment<K> {
  iloKey: K;
  iloIndex: number;
  tlas: PairInfo<K>[];
  ats: PairInfo<K>[];
}

// Bipartite Alignment-Sicht pro ILO: welche TLAs bereiten vor, welche ATs
// prüfen — jeweils mit Bloom-Distanz-Severity des Paars.
export function computeAlignment<K>(input: {
  ilos: IloInput[];
  tlas: TlaInput<K>[];
  ats: AtInput<K>[];
  iloKey: (index: number) => K;
  tlaKey: (index: number) => K;
  atKey: (index: number) => K;
}): IloAlignment<K>[] {
  const { ilos, tlas, ats, iloKey, tlaKey, atKey } = input;
  const blooms = ilos.map(iloBloom);

  const pair = (otherBloom: number | null, iloIdx: number, key: K): PairInfo<K> => {
    const base = blooms[iloIdx];
    const distance =
      base !== null && otherBloom !== null ? Math.abs(base - otherBloom) : null;
    return { key, distance, severity: severityFromDistance(distance) };
  };

  return ilos.map((_, iloIdx) => ({
    iloKey: iloKey(iloIdx),
    iloIndex: iloIdx,
    tlas: tlas.flatMap((tla, tlaIdx) =>
      tla.linked_ilo_keys.includes(iloKey(iloIdx))
        ? [pair(maxBloom(tla.bloom_levels, tla.type, TLA_TYPE_TO_BLOOM), iloIdx, tlaKey(tlaIdx))]
        : [],
    ),
    ats: ats.flatMap((at, atIdx) =>
      at.linked_ilo_keys.includes(iloKey(iloIdx))
        ? [pair(maxBloom(at.bloom_levels, at.type, AT_TYPE_TO_BLOOM), iloIdx, atKey(atIdx))]
        : [],
    ),
  }));
}

// Live-Hinweise aus dem aktuellen State (Wizard: linked_ilo_indices,
// Editor: linked_ilo_ids). Abdeckungs-Regeln spiegeln matrix.py:coverage(),
// Distanz-Regeln die Matrix-Zellen (nur verlinkte Paare, Distanz ≥ 2).
export function computeHints<K>(input: {
  ilos: IloInput[];
  tlas: TlaInput<K>[];
  ats: AtInput<K>[];
  iloKey: (index: number) => K;
  tlaKey: (index: number) => K;
  atKey: (index: number) => K;
}): Hint<K>[] {
  const { ilos, tlas, ats, iloKey, tlaKey, atKey } = input;
  const hints: Hint<K>[] = [];

  ilos.forEach((_, iloIdx) => {
    const key = iloKey(iloIdx);
    if (!tlas.some((tla) => tla.linked_ilo_keys.includes(key))) {
      hints.push({ code: "ilo_without_tla", severity: "tension", iloKey: key, step: 3 });
    }
    if (!ats.some((at) => at.linked_ilo_keys.includes(key))) {
      hints.push({ code: "ilo_unassessed", severity: "misalignment", iloKey: key, step: 4 });
    }
  });
  tlas.forEach((tla, tlaIdx) => {
    if (tla.linked_ilo_keys.length === 0) {
      hints.push({ code: "tla_without_ilo", severity: "tension", tlaKey: tlaKey(tlaIdx), step: 3 });
    }
  });
  ats.forEach((at, atIdx) => {
    if (at.linked_ilo_keys.length === 0) {
      hints.push({ code: "at_without_ilo", severity: "misalignment", atKey: atKey(atIdx), step: 4 });
    }
  });

  const blooms = ilos.map(iloBloom);
  const distanceHint = (
    kind: "tla" | "at",
    otherIdx: number,
    otherBloom: number | null,
    iloIdx: number,
  ) => {
    const base = blooms[iloIdx];
    if (base === null || otherBloom === null) return;
    const distance = Math.abs(base - otherBloom);
    const severity = severityFromDistance(distance);
    if (severity === "tension" || severity === "misalignment") {
      hints.push({
        code: kind === "tla" ? "bloom_distance_ilo_tla" : "bloom_distance_ilo_at",
        severity,
        iloKey: iloKey(iloIdx),
        ...(kind === "tla" ? { tlaKey: tlaKey(otherIdx) } : { atKey: atKey(otherIdx) }),
        distance,
        step: kind === "tla" ? 3 : 4,
      });
    }
  };
  tlas.forEach((tla, tlaIdx) => {
    const b = maxBloom(tla.bloom_levels, tla.type, TLA_TYPE_TO_BLOOM);
    tla.linked_ilo_keys.forEach((key) => {
      const iloIdx = ilos.findIndex((_, i) => iloKey(i) === key);
      if (iloIdx >= 0) distanceHint("tla", tlaIdx, b, iloIdx);
    });
  });
  ats.forEach((at, atIdx) => {
    const b = maxBloom(at.bloom_levels, at.type, AT_TYPE_TO_BLOOM);
    at.linked_ilo_keys.forEach((key) => {
      const iloIdx = ilos.findIndex((_, i) => iloKey(i) === key);
      if (iloIdx >= 0) distanceHint("at", atIdx, b, iloIdx);
    });
  });

  return hints;
}
